import Image from 'next/image';
import { useAtomValue } from 'jotai';
import { FlagIcon } from '@heroicons/react/24/outline';

import { useModal } from '@restworld/utils-common';
import { Heading, ProgressBar, Alert } from '@restworld/ui-ds-base';
import { PresentationCard, getCountryFlagUrl } from '@restworld/ui-components';

import { CardWrapper, EditButton } from './card-wrapper';
import { workerLanguagesAtom } from '../global-state.worker-data';
import { ProfileEditModal } from './profile-edit';
import { animated, useSpring } from '@react-spring/web';
import { useProfileCommonIntl } from './hooks';

export const LanguageSection = () => {
  const { missingInfoText, missingInfo: errorMessage, translate: t } = useProfileCommonIntl();

  const workerLanguages = useAtomValue(workerLanguagesAtom);
  const { show: showModal, open: openModal, close: closeModal } = useModal();
  const { isWorkerLanguageInfoMissing } = useIsWorkerLanguageInfoMissing();

  const itemsCount = workerLanguages?.length || 0;

  const viewContentStyles = useSpring({
    opacity: !showModal ? 1 : 0,
    transform: !showModal ? 'translateY(0%)' : 'translateY(5px)',
    config: { tension: 250, friction: 20 },
  });

  const editContentStyles = useSpring({
    opacity: showModal ? 1 : 0,
    transform: showModal ? 'translateY(0%)' : 'translateY(5px)',
    config: { tension: 250, friction: 20 },
  });
  workerLanguages?.sort((a, b) => {
    const aLevel = a?.language_level_cefr_var?.order;
    const bLevel = b?.language_level_cefr_var?.order;
    return aLevel && bLevel ? bLevel - aLevel : 0;
  });
  return (
    <CardWrapper variant="add" title={t('common:languages')} editPageType="languages">
      <div id="languages" className="flex flex-col items-start pt-4">
        {!showModal && (
          <animated.div style={viewContentStyles} className="flex lg:flex-row flex-col lg:gap-12 gap-6 w-full">
            {isWorkerLanguageInfoMissing && <Alert text={missingInfoText} className="mb-4" />}
            <div className="flex flex-1 flex-col items-start gap-4">
              {workerLanguages?.map((language, index) => {
                const label = (
                  <div className="flex flex-row justify-between items-center w-full">
                    <Heading size={5} color="muted" weight="medium">
                      {language?.language_var?.label}
                    </Heading>
                    <Heading size={5} color="muted" weight="normal">
                      {language?.language_level_cefr_var?.label}
                    </Heading>
                  </div>
                );
                const level = language?.language_level_cefr_var?.order;
                const percent = level ? (level / 6) * 100 : 0;
                return (
                  <PresentationCard
                    key={index}
                    label={label}
                    header={level ? <ProgressBar percent={percent} /> : errorMessage}
                    isError={!level}
                    icon={
                      <CountryFlag countryShortKey={language?.language_var.key as keyof typeof countryShortKeyMap} />
                    }
                    contentStyling={'gap-2'}
                  />
                );
              })}
            </div>
            {itemsCount !== 0 && (
              <div className="flex flex-row items-start lg:justify-start justify-end lg:w-[95px] lg:min-w-[95px] w-full">
                <EditButton
                  onClick={() => {
                    openModal();
                  }}
                />
              </div>
            )}
          </animated.div>
        )}
        {showModal && (
          <animated.div style={editContentStyles} className="w-full">
            <ProfileEditModal
              showModal={showModal}
              onClose={closeModal}
              editPageType="languages"
              editPageTitle={t('common:languages')}
              mode="edit"
            />
          </animated.div>
        )}
      </div>
    </CardWrapper>
  );
};
const CountryFlag = ({ countryShortKey }: { countryShortKey?: keyof typeof countryShortKeyMap }) => {
  const countyImgUrl = countryShortKeyMap[countryShortKey];
  return countyImgUrl ? (
    <Image alt={countryShortKey} src={getCountryFlagUrl(countyImgUrl)} height={24} width={24} />
  ) : (
    <FlagIcon className="w-6 h-6 text-primary-700" />
  );
};
const countryShortKeyMap = {
  russian: 'RU',
  english: 'GB',
  italian: 'IT',
  spanish: 'ES',
  french: 'FR',
  german: 'DE',
};

export function useIsWorkerLanguageInfoMissing() {
  const workerLanguages = useAtomValue(workerLanguagesAtom);
  const isWorkerLanguageInfoMissing = !workerLanguages || workerLanguages.length === 0;

  return { isWorkerLanguageInfoMissing };
}
